import moment from "moment";
import {
  get_all_tide_station,
  get_station_tide,
} from "../api/meteorologicalSea.js";
import { kvToJson } from "@/utils/dafaFormat.js";
import { feature } from "@turf/turf";
import { parse } from "wellknown";

// 港口潮汐图层 ID
const TIDAL_PORT_LAYER_ID = "ehh-tidal-port";
const TIDAL_PORT_SOURCE_ID = "ehh-tidal-port-source";
const TIDAL_PORT_ICON = "tidal-port-icon";
const TIDAL_PORT_ICON_FOCUS = "tidal-port-icon-focus";
// 文字显示的最小层级：低于该层级时隐藏图层文字，仅显示图标
const TIDAL_PORT_TEXT_MIN_ZOOM = 8;

const listeners = {
  onClick: null,
};

function isSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export default {
  state: {
    visible: false,
    activePortId: null,
    loading: false,
    harbourList: [],
    portTideData: {},
    timeStr: "",
    focusFeature: null,
  },
  getters: {
    tideData(state) {
      if (!state.portTideData || !state.portTideData.tides) return {};
      const _data = {};
      let date = null;
      let lastIndex = 0;
      const data = kvToJson(
        state.portTideData.tides.k,
        state.portTideData.tides.v
      );
      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        if (date === null) {
          date = item.forecastTime;
        } else if (
          !isSameDay(
            new Date(Number(item.forecastTime)),
            new Date(Number(date))
          )
        ) {
          const val = data.slice(lastIndex, i).map((item) => ({
            ...item,
            xAxis: moment(Number(item.forecastTime)).format("HH:mm"),
            key: moment(Number(item.forecastTime)).format("YYYY-MM-DD"),
            forecastTime: moment(Number(item.forecastTime)).format(
              "YYYY-MM-DD HH:mm:ss"
            ),
          }));
          _data[val[0].key] = val;
          date = null;
          lastIndex = i;
        }
      }
      // 处理最后一段
      if (lastIndex < data.length) {
        const val = data.slice(lastIndex).map((item) => ({
          ...item,
          xAxis: moment(Number(item.forecastTime)).format("HH:mm"),
          key: moment(Number(item.forecastTime)).format("YYYY-MM-DD"),
          forecastTime: moment(Number(item.forecastTime)).format(
            "YYYY-MM-DD HH:mm:ss"
          ),
        }));
        _data[val[0].key] = val;
      }
      return _data;
    },
    timeOptions(state, getters) {
      return Object.keys(getters.tideData);
    },
    // 日出日落数据数组，与 timeOptions 保持同序（与 tides 按天一一对应）
    sunList(state) {
      if (!state.portTideData || !state.portTideData.suns) return [];
      return kvToJson(state.portTideData.suns.k, state.portTideData.suns.v);
    },
  },
  actions: {
    // 获取港口列表
    getHarbourList({ state }) {
      if (state.harbourList.length !== 0) return Promise.resolve();
      return new Promise((resolve, reject) => {
        get_all_tide_station()
          .then((response) => {
            const result = response?.result || response?.data || {};
            state.harbourList = result;
            resolve();
          })
          .catch((e) => reject(e));
      });
    },
    // 初始化港口潮汐图层（加载图标 + 创建 source/layer）
    tidePortInit(context, map) {
      if (!map || window.map !== map) return Promise.resolve();
      return new Promise((resolve) => {
        // 加载港口潮汐图标
        const iconList = [
          {
            name: TIDAL_PORT_ICON,
            url: require("@/assets/images/swqx/port/tidal_port2.png"),
          },
          {
            name: TIDAL_PORT_ICON_FOCUS,
            url: require("@/assets/images/swqx/port/tidal-port-focus2.png"),
          },
        ];
        const promises = iconList.map((item) => {
          if (map.hasImage(item.name)) return Promise.resolve();
          return new Promise((resolveImg) => {
            map.loadImage(item.url, (err, img) => {
              if (
                !err &&
                map &&
                window.map === map &&
                !map.hasImage(item.name)
              ) {
                map.addImage(item.name, img);
              }
              resolveImg();
            });
          });
        });
        Promise.all(promises).then(() => {
          if (!map.getSource(TIDAL_PORT_SOURCE_ID)) {
            map.addSource(TIDAL_PORT_SOURCE_ID, {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: [],
              },
            });
          }
          if (!map.getLayer(TIDAL_PORT_LAYER_ID)) {
            map.addLayer({
              id: TIDAL_PORT_LAYER_ID,
              type: "symbol",
              source: TIDAL_PORT_SOURCE_ID,
              layout: {
                "icon-image": TIDAL_PORT_ICON,
                "icon-size": 0.4,
                "icon-anchor": "bottom",
                "icon-allow-overlap": true,
                "icon-ignore-placement": true,
                "text-field": [
                  "step",
                  ["zoom"],
                  "",
                  TIDAL_PORT_TEXT_MIN_ZOOM,
                  ["get", "name"],
                ],
                "text-size": 14,
                "text-offset": [0, 1],
                "text-max-width": 12,
                "text-allow-overlap": true,
                "text-ignore-placement": true,
                "text-optional": true,
                visibility: "none",
              },
              paint: {
                "text-color": "#000000",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1,
              },
            });
          }
          resolve();
        });
      });
    },
    // 显示港口潮汐图层（渲染港口要素）
    tidePortShow({ state, dispatch }) {
      const map = window.map;
      if (!map) return;
      // 渲染港口要素到图层
      const features = state.harbourList
        .map((item) => {
          if (!item.geom) return null;
          try {
            const _feature = feature(parse(item.geom), {
              ...item,
              name: item.name,
            });
            return { ..._feature, id: item.id + "tidal" };
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean);
      if (map.getSource(TIDAL_PORT_SOURCE_ID)) {
        map.getSource(TIDAL_PORT_SOURCE_ID).setData({
          type: "FeatureCollection",
          features,
        });
      }
      if (map.getLayer(TIDAL_PORT_LAYER_ID)) {
        map.setLayoutProperty(TIDAL_PORT_LAYER_ID, "visibility", "visible");
      }
      dispatch("tidePortAddListener");
    },
    tidePortHide({ dispatch }) {
      const map = window.map;
      if (!map) return;
      dispatch("tidePortRemoveListener");
      if (map.getLayer(TIDAL_PORT_LAYER_ID)) {
        map.setLayoutProperty(TIDAL_PORT_LAYER_ID, "visibility", "none");
      }
    },
    tidePortDestroy({ state }, map) {
      if (!map || !map.getLayer) return;
      if (map.getLayer(TIDAL_PORT_LAYER_ID)) {
        map.removeLayer(TIDAL_PORT_LAYER_ID);
      }
      if (map.getSource(TIDAL_PORT_SOURCE_ID)) {
        map.removeSource(TIDAL_PORT_SOURCE_ID);
      }
      state.focusFeature = null;
    },
    tidePortAddListener({ dispatch }) {
      const map = window.map;
      if (!map) return;
      listeners.onClick = (e) => {
        const data = e.features[0].toJSON();
        dispatch("tidePortHandleClick", data);
      };
      map.on("click", TIDAL_PORT_LAYER_ID, listeners.onClick);
    },
    tidePortRemoveListener() {
      const map = window.map;
      if (!map) return;
      if (listeners.onClick) {
        map.off("click", TIDAL_PORT_LAYER_ID, listeners.onClick);
      }
    },
    tidePortHandleClick({ state, dispatch }, data) {
      const portId = data.properties.id;
      const code = data.properties.code;
      state.activePortId = portId;
      state.visible = true;
      // 飞行到港口位置
      const map = window.map;
      if (map && data.geometry && data.geometry.coordinates) {
        const zoom = map.getZoom() > 15 ? map.getZoom() : 15;
        map.flyTo({
          center: data.geometry.coordinates,
          zoom,
          speed: 3,
          essential: true,
        });
      }
      // 设置焦点图标
      if (map && map.getLayer(TIDAL_PORT_LAYER_ID)) {
        map.setLayoutProperty(
          TIDAL_PORT_LAYER_ID,
          "icon-image",
          TIDAL_PORT_ICON_FOCUS
        );
      }
      state.focusFeature = data;
      // 获取潮汐数据
      return dispatch("getPortTide", code);
    },
    getPortTide({ state }, code) {
      return new Promise((resolve) => {
        state.loading = true;
        get_station_tide(code)
          .then((response) => {
            // h5 request 返回响应体本身，潮汐数据在 result 字段下
            state.portTideData = response?.result || response?.data || {};
            state.loading = false;
            // 默认选择第一个日期（按天分组后的首个日期 key，与 tideData getter 一致）
            const tidesArr = kvToJson(
              state.portTideData.tides?.k,
              state.portTideData.tides?.v
            );
            if (Array.isArray(tidesArr) && tidesArr.length) {
              const firstTime = Number(tidesArr[0].forecastTime);
              if (Number.isFinite(firstTime)) {
                state.timeStr = moment(firstTime).format("YYYY-MM-DD");
              }
            }
            resolve();
          })
          .catch(() => {
            state.loading = false;
            resolve();
          });
      });
    },
    changeDay({ state }, val) {
      state.timeStr = val;
    },
    tideForecastHide({ state }) {
      state.visible = false;
      state.activePortId = null;
      const map = window.map;
      if (map && map.getLayer(TIDAL_PORT_LAYER_ID)) {
        map.setLayoutProperty(
          TIDAL_PORT_LAYER_ID,
          "icon-image",
          TIDAL_PORT_ICON
        );
      }
      state.focusFeature = null;
    },
    // 获取指定日期的潮汐数据
    getTideDataForKey({ state }, key) {
      if (!state.portTideData || !state.portTideData.tides) return [];
      const data = kvToJson(
        state.portTideData.tides.k,
        state.portTideData.tides.v
      );
      let date = null;
      let lastIndex = 0;
      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        if (date === null) {
          date = item.forecastTime;
        } else if (
          !isSameDay(
            new Date(Number(item.forecastTime)),
            new Date(Number(date))
          )
        ) {
          const dayKey = moment(Number(date)).format("YYYY-MM-DD");
          if (dayKey === key) {
            return data.slice(lastIndex, i).map((item) => ({
              ...item,
              xAxis: moment(Number(item.forecastTime)).format("HH:mm"),
              key: dayKey,
            }));
          }
          date = null;
          lastIndex = i;
        }
      }
      // 处理最后一段
      if (lastIndex < data.length && date !== null) {
        const dayKey = moment(Number(date)).format("YYYY-MM-DD");
        if (dayKey === key) {
          return data.slice(lastIndex).map((item) => ({
            ...item,
            xAxis: moment(Number(item.forecastTime)).format("HH:mm"),
            key: dayKey,
          }));
        }
      }
      return [];
    },
  },
};
